import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import spriteSheet from '../../static/images/characters/grizzy_walk_sheet.png';

const WALK_FRAME_MS = 120;
const TOTAL_FRAMES = 8;
const WALK_DURATION = 1.5; // seconds — long enough for ~12 frames to show

// Sprite sheet natural dimensions: 1442 × 173 px (8 frames)
const SHEET_NATURAL_W = 1442;
const SHEET_NATURAL_H = 173;

// Display height scaled to fit the 80px-tall SVG (feet rest on node line at y=40)
const CHAR_H = 40;
const SHEET_DISPLAY_W = SHEET_NATURAL_W * (CHAR_H / SHEET_NATURAL_H);
const CHAR_W = SHEET_DISPLAY_W / TOTAL_FRAMES;

const NODE_X = { 1: 50, 2: 150, 3: 250, 4: 350, 5: 450, 6: 550, 7: 650 };

function clampNode(n) {
  return Math.min(7, Math.max(1, parseInt(n) || 1));
}

function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => { savedCallback.current = callback; }, [callback]);
  useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

/**
 * ProgressMap
 *
 * Props:
 *   characterNode — current destination node (1–7)
 *   fromNode      — optional: if provided and different from characterNode,
 *                   the character walks from fromNode → characterNode on mount.
 *                   Use this on the Level Complete screen to show progression.
 */
function ProgressMap({ characterNode = 1, fromNode = null }) {
  const node = clampNode(characterNode);
  const [frame, setFrame] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const prevNodeRef = useRef(node);
  const controls = useAnimation();
  const clipId = React.useId();

  const charX = NODE_X[node] - CHAR_W / 2;

  // Resolve starting x for the initial render
  const resolvedFrom = fromNode !== null ? clampNode(fromNode) : null;
  const initialX =
    resolvedFrom !== null && resolvedFrom !== node
      ? NODE_X[resolvedFrom] - CHAR_W / 2
      : charX;

  // On mount: if fromNode triggers a walk, start it
  useEffect(() => {
    if (resolvedFrom !== null && resolvedFrom !== node) {
      setIsMoving(true);
      controls.start({
        x: charX,
        transition: { type: 'tween', duration: WALK_DURATION, ease: 'linear' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While mounted: react to characterNode prop changes
  useEffect(() => {
    if (node !== prevNodeRef.current) {
      const targetX = NODE_X[node] - CHAR_W / 2;
      prevNodeRef.current = node;
      setIsMoving(true);
      controls.start({
        x: targetX,
        transition: { type: 'tween', duration: WALK_DURATION, ease: 'linear' },
      });
    }
  }, [node, controls]);

  // Only cycle frames while walking
  useInterval(() => setFrame(f => (f + 1) % TOTAL_FRAMES), isMoving ? WALK_FRAME_MS : null);

  return (
    <svg viewBox="0 0 700 80" width="100%" style={{ display: 'block' }}>
      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={CHAR_W} height={CHAR_H} />
        </clipPath>
      </defs>

      <rect x={0} y={0} width={700} height={80} fill="#0f172a" />

      {Object.values(NODE_X).slice(0, -1).map((x, i) => (
        <line
          key={i}
          x1={x} y1={40}
          x2={Object.values(NODE_X)[i + 1]} y2={40}
          stroke="#64748b" strokeWidth={2}
        />
      ))}

      {Object.entries(NODE_X).map(([level, x]) => (
        <g key={level}>
          <circle
            cx={x} cy={40} r={12}
            fill={parseInt(level) <= node ? "#4f46e5" : "#334155"}
          />
          <text x={x} y={44} textAnchor="middle" fill="#f1f5f9" fontSize={10}>
            {level}
          </text>
        </g>
      ))}

      <motion.g
        initial={{ x: initialX }}
        animate={controls}
        onAnimationComplete={() => {
          setIsMoving(false);
          setFrame(0);
        }}
      >
        <g clipPath={`url(#${clipId})`} transform={`translate(0, ${40 - CHAR_H})`}>
          <image
            href={spriteSheet}
            x={-(frame * CHAR_W)}
            y={0}
            width={SHEET_DISPLAY_W}
            height={CHAR_H}
            style={{ imageRendering: 'crisp-edges' }}
          />
        </g>
      </motion.g>
    </svg>
  );
}

export default ProgressMap;
